import React from 'react';
import {
  Box,
  Container,
  Text,
  Flex,
  Link,
  HStack,
  Image,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { Header } from '../../atoms/Header';
import { RevealOnScroll } from '../../atoms/RevealOnScroll';
import { imageZoomSx } from 'src/features/MP/utils/imageZoomSx';
import { Icon } from 'src/components/atoms/Icon';
import { nowArticles } from 'src/features/MP/utils/constants';

// Article Card Component
const ArticleCard: React.FC<{
  title: string;
  description: string;
  author?: string;
  company?: string;
  date: string;
  image?: string;
  variant?: 'now' | 'changelog' | 'customer';
  link?: string;
}> = ({
  title,
  description,
  author,
  company,
  date,
  image,
  variant = 'now',
  link,
}) => {
  const navigate = useNavigate();

  const handleClick = () => {
    if (link) {
      navigate(link);
    }
  };

  return (
    <Box
      bg="white"
      overflow="hidden"
      cursor={link ? 'pointer' : 'default'}
      shadow={variant === 'changelog' ? 'md' : 'none'}
      borderRadius={variant === 'changelog' ? '16px' : '0px'}
      onClick={handleClick}>
      <Box
        h={{
          base: variant === 'changelog' ? '180px' : '240px',
          md: variant === 'changelog' ? '200px' : '100%',
        }}
        position="relative"
        borderRadius="16px"
        overflow="hidden">
        {variant === 'changelog' ? (
          <Box
            position="relative"
            height={{
              base: variant === 'changelog' ? '180px' : '240px',
              md: variant === 'changelog' ? '200px' : '320px',
            }}
            borderRadius={
              variant === 'changelog' ? '16px 16px 0px 0px' : '16px'
            }
            backgroundImage={`url(${image})`}
            backgroundSize="cover"
          />
        ) : (
          <Image
            sx={imageZoomSx}
            src={image}
            borderRadius="16px"
            width="100%"
            height="350px"
            objectFit="cover"
          />
        )}
      </Box>

      <Box
        mt={variant === 'changelog' ? '0px' : { base: '16px', md: '24px' }}
        p={variant === 'changelog' ? { base: '16px', md: '24px' } : '0px'}>
        <HStack spacing={2} mb={{ base: 3, md: 4 }} flexWrap="wrap">
          {author && (
            <Text
              fontSize={{ base: '14px', md: '15px' }}
              color="gray.600"
              fontWeight="medium">
              By {author}
            </Text>
          )}
          {variant === 'customer' && (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 18 18"
              fill="none">
              <path
                d="M5.25 3V15L15 9L5.25 3Z"
                stroke="#27AE60"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
          {company && (
            <Text
              fontSize={{ base: '14px', md: '15px' }}
              color="gray.600"
              fontWeight="medium">
              {company}
            </Text>
          )}
          <Text fontSize={{ base: '14px', md: '15px' }} color="gray.500">
            {variant === 'changelog' ? '' : '•'} {date}
          </Text>
        </HStack>

        <Text
          fontSize={{
            base: variant === 'now' ? '18px' : '14px',
            md: variant === 'now' ? '21px' : '15px',
          }}
          fontWeight="bold"
          color="text.darkBlue.900"
          mb={{ base: 3, md: 4 }}
          lineHeight="1.3"
          fontFamily="marketingPage"
          textTransform="uppercase">
          {title}
        </Text>

        <Text
          fontSize={{ base: '14px', md: '15px' }}
          color="gray.600"
          lineHeight="1.5"
          fontFamily="marketingPage"
          noOfLines={2}>
          {description}
        </Text>
      </Box>
    </Box>
  );
};

// Section Header Component
const SectionHeader: React.FC<{
  title: string;
  subtitle?: string;
  linkText?: string;
  linkHref?: string;
}> = ({ title, subtitle, linkText, linkHref }) => {
  return (
    <Flex
      direction={{ base: 'column', md: 'row' }}
      justify="space-between"
      align={{ base: 'flex-start', md: 'center' }}
      mb={{ base: '24px', md: '32px' }}
      gap={4}>
      <Box>
        <Header
          fontSize={{ base: '32px', md: '40px' }}
          mb={{ base: 3, md: 4 }}
          textTransform="none">
          {title}
        </Header>
        {subtitle && (
          <Text
            fontSize={{ base: '14px', md: '15px' }}
            color="gray.600"
            fontFamily="marketingPage"
            fontWeight="normal"
            textTransform="none">
            {subtitle}
          </Text>
        )}
      </Box>

      {linkText && linkHref && (
        <Link
          href={'#'}
          color="gray.600"
          fontSize="16px"
          fontWeight="medium"
          _hover={{ textDecoration: 'underline' }}
          display="flex"
          alignItems="center"
          gap={2}>
          {linkText}
          <Icon iconName="chevron-right" w={'18px'} h={'18px'} />
        </Link>
      )}
    </Flex>
  );
};

const NowPage: React.FC = () => {
  return (
    <Box color="text.darkBlue.900" bg="white" position="relative">
      <Container py={{ base: 8, md: 16 }} maxW="container.xl">
        {/* NOW Section */}
        <SectionHeader
          title="NOW"
          subtitle="Merkatus.io stories – from blogs to product updates and customer wins"
        />

        {/* First row of NOW cards */}
        <RevealOnScroll>
          <Flex
            direction={{ base: 'column', md: 'row' }}
            gap={{ base: '24px', md: '48px' }}
            mb={{ base: '32px', md: '64px' }}
            position="relative">
            {/* Vertical separator for first row */}
            <Box
              position="absolute"
              left="50%"
              top="0"
              bottom="0"
              width="1px"
              bg="gray.200"
              opacity={0.6}
              transform="translateX(-50%)"
              display={{ base: 'none', md: 'block' }}
            />

            {nowArticles.slice(0, 2).map((article) => (
              <Box key={article.id} width={{ base: '100%', md: '50%' }}>
                <ArticleCard {...article} variant="now" />
              </Box>
            ))}
          </Flex>
        </RevealOnScroll>

        {/* Second row of NOW cards */}
        <RevealOnScroll>
          <Flex
            direction={{ base: 'column', md: 'row' }}
            gap={'48px'}
            mb={'64px'}
            position="relative">
            {/* Vertical separator for second row */}
            <Box
              position="absolute"
              left="50%"
              top="0"
              bottom="0"
              width="1px"
              bg="gray.200"
              opacity={0.6}
              transform="translateX(-50%)"
              display={{ base: 'none', md: 'block' }}
            />

            {nowArticles.slice(2, 4).map((article) => (
              <Box key={article.id} width={{ base: '100%', md: '50%' }}>
                <ArticleCard {...article} variant="now" />
              </Box>
            ))}
          </Flex>
        </RevealOnScroll>

        {/* Third row of NOW cards */}
        <RevealOnScroll>
          <Flex
            direction={{ base: 'column', md: 'row' }}
            gap={'48px'}
            mb={'64px'}
            position="relative">
            {nowArticles.slice(4, 6).map((article) => (
              <Box
                key={article.id}
                width={{ base: '100%', md: '50%' }}
                pr={{ base: '0px', md: '24px' }}>
                <ArticleCard {...article} variant="now" />
              </Box>
            ))}
          </Flex>
        </RevealOnScroll>

        {/* Horizontal separator */}
        {/* <Box
          width="100%"
          height="1px"
          bg="gray.200"
          opacity={0.6}
          mb={'64px'}
        /> */}

        {/* Changelog Section */}
        {/* <SectionHeader
          title="Changelog"
          linkText="All updates"
          linkHref="/changelog"
        />

        <SimpleGrid columns={{ base: 1, md: 4 }} spacing={'24px'} mb={16}>
          {changelogUpdates.map((update) => (
            <ArticleCard key={update.id} {...update} variant="changelog" />
          ))}
        </SimpleGrid> */}

        {/* Horizontal separator */}
        {/* <Box
          width="100%"
          height="1px"
          bg="gray.200"
          opacity={0.6}
          mb={'64px'}
        /> */}

        {/* Additional Updates Section */}
        {/* <SectionHeader
          title="Additional Updates"
          linkText="All updates"
          linkHref="/additional-updates"
        /> */}

        {/* First row of Additional Updates cards */}
        {/* <Flex
          direction={{ base: 'column', md: 'row' }}
          gap={'48px'}
          mb={8}
          position="relative"> */}
        {/* Vertical separator for first row */}
        {/* <Box
            position="absolute"
            left="50%"
            top="0"
            bottom="0"
            width="1px"
            bg="gray.200"
            opacity={0.6}
            transform="translateX(-50%)"
            display={{ base: 'none', md: 'block' }}
          />

          {additionalUpdates.slice(0, 2).map((update) => (
            <Box key={update.id} flex="1">
              <ArticleCard {...update} variant="now" />
            </Box>
          ))}
        </Flex> */}

        {/* Second row of Additional Updates cards */}
        {/* <Flex
          direction={{ base: 'column', md: 'row' }}
          gap={'48px'}
          mb={'64px'}
          position="relative"> */}
        {/* Vertical separator for second row */}
        {/* <Box
            position="absolute"
            left="50%"
            top="0"
            bottom="0"
            width="1px"
            bg="gray.200"
            opacity={0.6}
            transform="translateX(-50%)"
            display={{ base: 'none', md: 'block' }}
          />

          {additionalUpdates.slice(2, 4).map((update) => (
            <Box key={update.id} flex="1">
              <ArticleCard {...update} variant="now" />
            </Box>
          ))}
        </Flex> */}

        {/* Horizontal separator */}
        {/* <Box
          width="100%"
          height="1px"
          bg="gray.200"
          opacity={0.6}
          mb={'64px'}
        /> */}

        {/* Customer Stories Section */}
        {/* <SectionHeader
          title="Customers stories"
          linkText="See all"
          linkHref="/customer-stories"
        />

        <Box mb={'64px'}>
          <Flex direction={{ base: 'column', md: 'row' }} gap={'48px'}> */}
        {/* First card */}
        {/* <Box flex="1">
              <ArticleCard {...customerStories[0]} variant="customer" />
            </Box> */}

        {/* Vertical separator */}
        {/* <Box
              width="1px"
              bg="gray.200"
              opacity={0.6}
              display={{ base: 'none', md: 'block' }}
            /> */}

        {/* Second card */}
        {/* <Box flex="1">
              <ArticleCard {...customerStories[1]} variant="customer" />
            </Box> */}

        {/* Vertical separator */}
        {/* <Box
              width="1px"
              bg="gray.200"
              opacity={0.6}
              display={{ base: 'none', md: 'block' }}
            /> */}

        {/* Third card */}
        {/* <Box flex="1">
              <ArticleCard {...customerStories[2]} variant="customer" />
            </Box>
          </Flex>
        </Box> */}

        {/* Horizontal separator */}
        {/* <Box
          width="100%"
          height="1px"
          bg="gray.200"
          opacity={0.6}
          mb={'64px'}
        /> */}

        {/* Additional Customer Stories Section */}
        {/* <SectionHeader
          title="Customers stories"
          linkText="See all"
          linkHref="/customer-stories"
        /> */}

        {/* <SimpleGrid columns={{ base: 1, md: 3 }} spacing={'24px'} mb={'64px'}>
          {additionalCustomerStories.map((story) => (
            <Box key={story.id} bg="white" overflow="hidden" cursor="pointer">
              <Box
                h="200px"
                bgGradient="linear(to-br, #2C3865, #1a202c)"
                display="flex"
                borderRadius={'16px 16px 0px 0px'}
                alignItems="center"
                justifyContent="center">
                <Text
                  color="white"
                  fontSize="2xl"
                  fontWeight="bold"
                  fontFamily="marketingPage">
                  LOGO
                </Text>
              </Box>

              <Box mt={'24px'}>
                <Text
                  fontSize="15px"
                  color="gray.600"
                  lineHeight="1.5"
                  fontFamily="marketingPage">
                  {story.description}
                </Text>
              </Box>
            </Box>
          ))}
        </SimpleGrid> */}
      </Container>
    </Box>
  );
};

export default NowPage;
